import { ActivityListener } from './effects/activity-listener';
import { BrowserLifecycleListeners } from './effects/browser-events';
import { DomObserver } from './effects/dom-observer';
import { IdlePoller } from './effects/idle-poller';
import { IntervalLoop } from './effects/interval-loop';
import { defaultTrackability, type ElementTrackability } from './effects/trackability';
import {
	createElementDwellSpansCache,
	rolloverActiveElementDwellSpans,
	type ElementDwellSpansCache,
} from './state/element-dwell-times';
import { getEmitPayload, type DwellEmitHandler } from './state/emit';
import { createIdleState, recordActivity, type IdleState } from './state/idle-state';
import {
	createIntervalScheduleCursor,
	resetIntervalScheduleCursor,
	type IntervalSchedule,
	type IntervalScheduleCursor,
} from './state/interval-schedule';
import {
	createDwellSessionState,
	endDwellSession,
	isDwellSessionActive,
	startDwellSession,
	type DwellSessionState,
} from './state/session';
import { generateSessionId } from './util/id';

import type { Clock } from './effects/types';

const DEFAULT_IDLE_TIMEOUT_SECONDS = 60;
const defaultClock: Clock = () => Date.now();

export interface DwellTrackerOptions {
	schedule: IntervalSchedule;
	onEmit: DwellEmitHandler;
	idleTimeoutSeconds?: number;
	/**
	 * How frequently (in ms) the idle-poller checks whether the user has gone
	 * idle. Defaults to 5 000 ms. Set this lower than `idleTimeoutSeconds * 1000`
	 * when a short idle timeout is required (e.g. `idleTimeoutSeconds: 2` needs
	 * `idlePollerIntervalMs: 500` or similar to avoid silent detection lag).
	 */
	idlePollerIntervalMs?: number;
	clock?: Clock;
	generateId?: () => string;
	/**
	 * Which DOM elements to track and how to derive their key/threshold.
	 * Accepts a single config or an array of them (each with its own
	 * `selector`/`getKey`/`intersectionThreshold`). Defaults to
	 * `defaultTrackability` (elements carrying a `data-dwell-id` attribute).
	 */
	trackability?: ElementTrackability | ElementTrackability[];
}

export class DwellTracker {
	private readonly schedule: IntervalSchedule;
	private readonly onEmit: DwellEmitHandler;
	private readonly idleTimeoutSeconds: number;
	private readonly clock: Clock;
	private readonly generateId: () => string;
	private readonly trackability: ElementTrackability[];

	private readonly spansCache: ElementDwellSpansCache = createElementDwellSpansCache();
	private readonly sessionState: DwellSessionState = createDwellSessionState();
	private readonly cursor: IntervalScheduleCursor = createIntervalScheduleCursor();
	private readonly idleState: IdleState = createIdleState();

	private readonly domObserver: DomObserver;
	private readonly activityListener: ActivityListener;
	private readonly browserLifecycle: BrowserLifecycleListeners;
	private readonly idlePoller: IdlePoller;
	private intervalLoop: IntervalLoop | undefined;

	private trackerRunning = false;

	public constructor(options: DwellTrackerOptions) {
		this.schedule = options.schedule;
		this.onEmit = options.onEmit;
		this.clock = options.clock ?? defaultClock;
		this.generateId = options.generateId ?? generateSessionId;
		this.idleTimeoutSeconds = options.idleTimeoutSeconds ?? DEFAULT_IDLE_TIMEOUT_SECONDS;

		this.trackability = normalizeTrackability(options.trackability);

		// Construct the persistent effects eagerly. Each one is inert until
		// its `start` is called from `tracker.start()`.
		this.domObserver = new DomObserver(this.spansCache, this.trackability, this.clock);
		this.activityListener = new ActivityListener(this.idleState, this.clock);
		this.browserLifecycle = new BrowserLifecycleListeners({
			onSessionEnd: this.handleSessionEnd,
			onSessionRestart: this.handleSessionRestart,
		});
		this.idlePoller = new IdlePoller(this.idleState, {
			idleTimeoutMs: this.idleTimeoutSeconds * 1000,
			pollIntervalMs: options.idlePollerIntervalMs,
			clock: this.clock,
			onIdle: () => this.closeSession(),
			onActive: () => this.openSession(),
		});
	}

	public start = () => {
		if (this.trackerRunning) {
			return;
		}
		this.trackerRunning = true;

		this.domObserver.start();
		this.activityListener.start();
		this.browserLifecycle.start();
		this.idlePoller.start();

		this.openSession();
	};

	public stop = () => {
		if (!this.trackerRunning) {
			return;
		}
		this.trackerRunning = false;

		this.closeSession();

		this.domObserver.stop();
		this.activityListener.stop();
		this.browserLifecycle.stop();
		this.idlePoller.stop();
	};

	private readonly openSession = () => {
		if (isDwellSessionActive(this.sessionState)) {
			return;
		}

		const now = this.clock();
		startDwellSession(this.sessionState, this.generateId(), now);
		resetIntervalScheduleCursor(this.cursor);
		recordActivity(this.idleState, now);
		// Rebase any spans that are still active from a previous session so
		// their entrance timestamps start at this session's opening moment.
		// Without this, the first interval would emit the full time-since-
		// last-close, including any idle/away gap.
		rolloverActiveElementDwellSpans(this.spansCache, now);

		this.intervalLoop = new IntervalLoop(this.schedule, this.cursor, (stepIndex) => {
			const intervalEndedAt = this.clock();
			this.flushEmit(intervalEndedAt, stepIndex);
			rolloverActiveElementDwellSpans(this.spansCache, intervalEndedAt);
		});
		this.intervalLoop.start();
	};

	private readonly closeSession = () => {
		if (!isDwellSessionActive(this.sessionState)) {
			return;
		}

		const intervalEndedAt = this.clock();
		this.flushEmit(intervalEndedAt, this.cursor.stepIndex);

		this.intervalLoop?.stop();
		this.intervalLoop = undefined;

		endDwellSession(this.sessionState);
		// Leave active spans alone — they'll be rebased when (if) a new
		// session opens. Drop completed spans now since they've been emitted.
		this.spansCache.completed = [];
		resetIntervalScheduleCursor(this.cursor);
	};

	private readonly handleSessionEnd = () => this.closeSession();
	private readonly handleSessionRestart = () => this.openSession();

	private readonly flushEmit = (intervalEndedAt: number, stepIndex: number) => {
		const payload = getEmitPayload(this.sessionState, this.spansCache, intervalEndedAt, stepIndex);
		if (payload === null) {
			return;
		}
		try {
			this.onEmit(payload);
		} catch (err) {
			console.error('[dwell-time] emit handler threw:', err);
		}
	};
}

function normalizeTrackability(
	trackability: ElementTrackability | ElementTrackability[] | undefined,
): ElementTrackability[] {
	if (trackability === undefined) {
		return [defaultTrackability];
	}
	return Array.isArray(trackability) ? trackability : [trackability];
}
